import { SwDerivedApiStore } from "@sourcewhale/shared/src/svelte/api-stores/sw-api-store";
import { swRequest } from "@sourcewhale/shared/src/svelte/api/sw-request";
import { derived, get, writable } from "svelte/store";
import { saveIntegrationSettings } from "../../../api/integrations";
import { getProviderLearnMoreLink } from "./providerLinks";

const getTypePrefix = (type) =>
  type ? `${type.charAt(0).toUpperCase()}${type.slice(1)}` : "general";

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const SETTING_KEYS = {
  status: (type) => [
    `${type}DefaultStatus`,
    `${type}UnsubscribeStatus`,
    ...(type === "Candidate" ? ["addToJobStatus"] : []),
  ],
  sync: ["syncWhen"],
  notes: ["onlyAddNotesAfterSync", "defaultNoteAction"],
  notContactAdvanced: [
    // These are included in the FieldSettingsTable component
    "displayedAdvancedOptions",
    "mandatoryFields",
    "protectedFields",
  ],
  general: [
    // Top section of contact settings
    "blockOutreachToOffLimits",
    "updateInResponseToChanges",
    // General displayed company information
    "companyDefaultStatus",
    "extensionCompanyInfo",
    "extensionCompanyWarn",
    // General job information
    "jobStatusWhitelist",
    "removeFlaggedEmails",
    "removeFlaggedPhones",
    // Activity Notes options
    "createNotesUpon",
  ],
  dataRefresh: { toggle: ["enabled"], multiChoice: ["syncAs"] },
};

const EMPTY_SETTINGS_BY_KIND = {
  checkbox: false,
  singleChoice: null,
  multiChoice: [],
  notFound: null,
};

export const activeProviderModalStore = writable(null);
export const allProvidersConfigStore = writable(null);
export const hasUnsavedChangesStore = writable(false);
export const currentProviderStore = writable(null);
export const isDataRefreshTab = writable(null);

export const providerConfigDraftStore = (() => {
  const store = writable({});
  let original = null;

  const checkChanges = (draft) => {
    if (
      (!original?.choices || !draft?.choices) &&
      (!original?.customMap || !draft?.customMap) &&
      (!original?.lockedChoices || !draft?.lockedChoices)
    ) {
      hasUnsavedChangesStore.set(false);
      return;
    }

    const changed = (key) =>
      JSON.stringify(original[key] || {}) !== JSON.stringify(draft[key] || {});

    hasUnsavedChangesStore.set(
      changed("choices") || changed("customMap") || changed("lockedChoices"),
    );
  };

  return {
    subscribe: (run) =>
      store.subscribe((value) => {
        checkChanges(value);
        run(value);
      }),
    set: (value) => store.set(deepClone(value)),
    update: (fn) => store.update((draft) => fn(deepClone(draft))),
    get: () => {
      let value;
      store.subscribe((v) => {
        value = v;
      })();
      return value;
    },
    initialize: ($modal, $config) => {
      if (!$modal || !$config) return;
      original = deepClone($config);
      store.set({
        choices: deepClone($config.choices) || {},
        customMap: deepClone($config.customMap) || {},
        lockedChoices: deepClone($config.lockedChoices) || {},
        ...($config.dataRefresh
          ? { dataRefresh: deepClone($config.dataRefresh) }
          : {}),
      });
    },
    reset: () => {
      original = null;
      store.set({});
    },
  };
})();

const updateProviderConfigDraftStore = (outerKey, innerKey, key, val) => {
  providerConfigDraftStore.update((draft) => {
    draft[outerKey] = draft[outerKey] || {};
    draft[outerKey][innerKey] = draft[outerKey][innerKey] || {};
    draft[outerKey][innerKey][key] = val;
    return draft;
  });
};

export const activeProviderConfigStore = derived(
  [activeProviderModalStore, allProvidersConfigStore],
  ([$modal, $all]) => ($modal && $all ? $all[$modal.provider] : null),
);

// Missing optionValues indicates there was an error we failed to catch in integrations/get_all
// In this case we want to show an error message in the modal, but still allow deactivation
export const activeProviderIsErrorStore = derived(
  [activeProviderModalStore, allProvidersConfigStore],
  ([$modal, $all]) =>
    $modal &&
    $all &&
    $all[$modal.provider] &&
    !$all[$modal.provider].optionValues,
);

export const integrationStore = derived(
  [
    activeProviderConfigStore,
    providerConfigDraftStore,
    activeProviderModalStore,
    isDataRefreshTab,
  ],
  ([$config, $draft, $modal, $dataRefresh]) => ({
    getSettings: (section, type) => {
      if (!$config?.optionValues) return [];
      const typePrefix = getTypePrefix(type);

      let keys = [];
      if (section === "status") {
        keys = SETTING_KEYS.status(type);
      } else if (section === "activityNotes") {
        keys = $config.optionValues
          .filter((opt) => opt.isNoteMapping)
          .map((opt) => opt.key);
      } else if (section === "notetaker") {
        keys = $config.optionValues
          .filter((opt) => opt.isNotetaker)
          .map((opt) => opt.key);
      } else if (section === "stages") {
        keys = $config.optionValues
          .filter((opt) => opt.isStageMapping)
          .map((opt) => opt.key);
      } else if (section === "noteCreation") {
        keys = $config.optionValues
          .filter((opt) => opt.isNoteCreation)
          .map((opt) => opt.key);
      } else if (SETTING_KEYS[section]) {
        // No advanced settings explicitly defined in SETTING_KEYS
        // The idea is `advanced` should include all settings not included in the main sections
        keys = SETTING_KEYS[section] || [];
      } else if (section === "generalAdvanced") {
        keys = $config.optionValues
          .filter(
            // Collect all settings tagged with `isGeneral`, but do not include
            // settings that are already included in the `general` section
            (opt) => opt.isGeneral && !SETTING_KEYS.general.includes(opt.key),
          )
          .map((opt) => opt.key);
      } else if (section === "contactAdvanced") {
        keys = $config.optionValues
          .filter(
            // Collect all settings without the `isGeneral` tag, but do not include
            // settings that are already included elsewhere.
            (opt) =>
              !opt.isGeneral &&
              !opt.isStageMapping &&
              !opt.isNoteMapping &&
              !opt.isNotetaker &&
              !SETTING_KEYS.status(type).includes(opt.key) &&
              !SETTING_KEYS.sync.includes(opt.key) &&
              !SETTING_KEYS.notes.includes(opt.key) &&
              !SETTING_KEYS.notContactAdvanced.includes(opt.key) &&
              !SETTING_KEYS.general.includes(opt.key) &&
              !SETTING_KEYS.dataRefresh.toggle.includes(opt.key) &&
              !SETTING_KEYS.dataRefresh.multiChoice.includes(opt.key),
          )
          .map((opt) => opt.key);
      }

      const isRestricted = (val) =>
        type &&
        val.syncAsRestrictions &&
        !val.syncAsRestrictions.includes(typePrefix);

      return $config.optionValues.filter((opt) => {
        if (!keys.includes(opt.key) || isRestricted(opt)) return false;
        if (opt.values) {
          const values = opt.values.filter((val) => !isRestricted(val));
          opt = { ...opt, values };
        }
        return true;
      });
    },

    getFields: (syncAs) => {
      const fieldsKey = $config.syncKinds.includes(syncAs)
        ? "candidateFields"
        : "companyFields";
      return Object.entries($config?.[fieldsKey] || {}).map(([key, value]) => ({
        ...value,
        key,
      }));
    },

    getDataRefreshFields: () => {
      const contactData = $draft.dataRefresh?.contactData === "enabled";
      return Object.entries($config?.dataRefreshFields || {})
        .map(([key, value]) => ({ ...value, key }))
        .filter((x) => !x.contactData || contactData);
    },

    getValue: (key, type) => {
      if (type === "dataRefresh") {
        return $draft.dataRefresh?.[key] ?? $config.dataRefresh?.[key];
      }

      // Regular handling for other types
      const typePrefix = getTypePrefix(type);
      const choices = $draft.choices || {};
      const value = choices[typePrefix]?.[key];

      const item = $config?.optionValues?.find((opt) => opt.key === key) || {};

      const kind = item.kind || "notFound";

      // "defaultIsUsed" should be set to true in the backend options only if
      // the listed default value is actually used when the user has not set a value.
      // e.g. "createAccounts" is true if no value is set, so we should show that on the frontend.
      const defaultValue = item.defaultIsUsed ? item.default : null;

      return value ?? defaultValue ?? EMPTY_SETTINGS_BY_KIND[kind];
    },

    getFieldValue: (key, syncAs) => {
      // Special handling for dataRefresh
      const base = $dataRefresh ? $draft.dataRefresh : $draft;
      const customMap = base?.customMap || {};
      return customMap[syncAs]?.[key];
    },

    setValue: (key, value, type) => {
      // Special handling for dataRefresh
      if (type === "dataRefresh") {
        providerConfigDraftStore.update((draft) => {
          // Only update at the root level
          draft.dataRefresh = draft.dataRefresh || {};
          draft.dataRefresh[key] = value;
          return draft;
        });
        return;
      }
      // Regular handling for other types
      const typePrefix = getTypePrefix(type);
      updateProviderConfigDraftStore("choices", typePrefix, key, value);
    },

    setFieldValue: (key, value, syncAs) => {
      // Special handling for dataRefresh
      if ($dataRefresh) {
        providerConfigDraftStore.update((draft) => {
          draft.dataRefresh = draft.dataRefresh || {};
          draft.dataRefresh.customMap = draft.dataRefresh.customMap || {};
          draft.dataRefresh.customMap[syncAs] =
            draft.dataRefresh.customMap[syncAs] || {};
          draft.dataRefresh.customMap[syncAs][key] = value;
          return draft;
        });
        return;
      }

      updateProviderConfigDraftStore("customMap", syncAs, key, value);
    },

    getFieldOptions: (syncAs) => $config.fields[syncAs] || [],

    getProviderName: () => $modal?.provider || "",

    getProviderTitle: () => $modal?.title || "",

    getLearnMoreLink: (page) =>
      page ? getProviderLearnMoreLink($modal?.provider, page) : null,

    getSyncAsLabelMap: () => $config?.syncAsLabelMap || {},

    getSyncAsSet: () => Object.keys($config?.fields) || [],

    getSyncKinds: () => $config?.syncKinds || [],

    getLockedChoice: (key, type) => {
      const typePrefix = getTypePrefix(type);
      return $draft.lockedChoices?.[typePrefix]?.[key] || false;
    },

    setLockedChoice: (key, value, type) => {
      const typePrefix = getTypePrefix(type);
      updateProviderConfigDraftStore("lockedChoices", typePrefix, key, value);
    },
  }),
);

// Helper functions
export const saveSettings = async (provider, settings) => {
  try {
    // If settings are not provided, use the current draft
    if (!settings) {
      const draft = providerConfigDraftStore.get();
      settings = {
        choices: draft.choices,
        customMap: draft.customMap,
        lockedChoices: draft.lockedChoices,
      };

      // Handle dataRefresh settings specially
      if (draft.dataRefresh) {
        settings.dataRefresh = draft.dataRefresh;
      }
    }

    const result = await saveIntegrationSettings(provider, settings);
    if (result) {
      // Update the allProvidersConfigStore with the new settings for this provider
      allProvidersConfigStore.update((allProviders) =>
        allProviders && allProviders[provider]
          ? {
              ...allProviders,
              [provider]: { ...allProviders[provider], ...settings },
            }
          : allProviders,
      );

      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to save integration settings:", error);
    return false;
  }
};

// Helper function to format time with unit
const relativeTimeWithUnit = (delta, unit, unitValue) => {
  const t = Math.round(delta / unitValue);
  return `${t}${unit} ago`;
};

// Helper function to format refresh time
const formatRefreshTime = (timestamp) => {
  if (!timestamp) return "Unknown";

  const now = Math.floor(Date.now() / 1000);
  const delta = now - timestamp;

  const second = 1;
  const minute = 60;
  const hour = 60 * 60;
  const day = 60 * 60 * 24;

  if (delta < minute) return relativeTimeWithUnit(delta, "s", second);

  if (delta < hour) return relativeTimeWithUnit(delta, "m", minute);

  // eslint-disable-next-line sourcewhale/prefer-ternary
  if (delta < day) return relativeTimeWithUnit(delta, "h", hour);

  return relativeTimeWithUnit(delta, "d", day);
};

const dataRefreshArgsStore = derived(
  [currentProviderStore],
  ([$currentProvider]) => ($currentProvider ? [$currentProvider] : null),
);

export const dataRefreshStore = SwDerivedApiStore({
  get: async (provider) => {
    const response = await swRequest({
      method: "GET",
      path: `/api/candidates/recent_data_refresh?provider=${provider}`,
    });

    const activeConfig = get(activeProviderConfigStore);
    const syncAsLabelMap = activeConfig?.syncAsLabelMap || {};

    let oldestTimestamp = null;
    if (response.data && response.data.length) {
      oldestTimestamp = Math.min(...response.data.map((x) => x.timestamp));
    }

    const oldestDate = oldestTimestamp
      ? new Date(oldestTimestamp * 1000).toLocaleDateString("en-GB")
      : "";

    return {
      recentRefreshes: response.data.map((item) => ({
        name: item.name,
        title: item.role,
        company: item.company,
        type: syncAsLabelMap[item.syncAs] || item.syncAs || "SourceWhale",
        refreshed: formatRefreshTime(item.timestamp),
      })),
      metrics: {
        updated: response.metrics?.updated || 0,
        verified: response.metrics?.verified || 0,
        updatedFields: response.metrics?.updatedFields || 0,
        verifiedFields: response.metrics?.verifiedFields || 0,
        oldestDate,
      },
    };
  },
  init: () => ({
    recentRefreshes: [],
    metrics: {
      updated: 0,
      verified: 0,
      updatedFields: 0,
      verifiedFields: 0,
      oldestDate: "",
    },
  }),
  argsStore: dataRefreshArgsStore,
});

export const deactivateIntegration = async (provider) =>
  swRequest({
    method: "POST",
    path: "/api/integrations/deactivate",
    body: { provider },
  });
